import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Cake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterMember() {
  const navigate = useNavigate();
  const { registerMember } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    church_code: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    dob: "",
    address: "",
  });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [defaultChurch, setDefaultChurch] = useState(null);
  const [defaultLoading, setDefaultLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/default-church`)
      .then((r) => {
        setDefaultChurch(r.data);
        if (r.data?.church_code) setForm((f) => ({ ...f, church_code: r.data.church_code }));
      })
      .catch(() => setDefaultChurch(null))
      .finally(() => setDefaultLoading(false));
  }, []);

  const hideCodeField = Boolean(defaultChurch?.church_code);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await registerMember({
        ...form,
        // Defensive normalization — see PasswordInput / iOS notes.
        // Trim+lowercase the email so an iOS-autocapitalized "John@…"
        // matches later "john@…" login attempts. Also trim first/last
        // name so a trailing-space AutoFill doesn't produce a member
        // named "Marc " that admins can't search for.
        email: (form.email || "").trim().toLowerCase(),
        first_name: (form.first_name || "").trim(),
        last_name: (form.last_name || "").trim(),
        church_code: form.church_code.toUpperCase(),
      });
      if (res?.status === "pending") { setPending(true); return; }
      toast.success("Welcome!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-shell" data-testid="register-member-page">
      <div className="px-5 pt-4 flex items-center justify-between">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-stone-500">
          <ArrowLeft className="w-3 h-3" /> {t("back")}
        </Link>
        <LanguageToggle />
      </div>

      {pending ? (
        <div className="px-5 mt-8 text-center space-y-3" data-testid="rm-pending">
          <div className="text-5xl">⏳</div>
          <h1 className="font-serif-display text-2xl">Almost there!</h1>
          <p className="text-sm text-stone-600 leading-relaxed">
            We couldn&apos;t auto-verify your name against the church roster, so the main admin will review your request. You&apos;ll receive access once they&apos;ve approved you.
          </p>
          <Link
            to="/login"
            className="inline-block mt-4 rounded-full px-5 py-2 text-white text-sm font-semibold"
            style={{ backgroundColor: "var(--tenant-primary)" }}
            data-testid="rm-pending-back-to-login"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
      <>
      <div className="px-5 mt-4">
        <h1 className="font-serif-display text-3xl mt-1 tracking-tight" data-testid="register-heading">
          {t("registerHeading")}
        </h1>
        <p className="text-sm text-stone-500 mt-2">
          {t("registerSubtitle")}
        </p>
      </div>
      <form onSubmit={submit} className="px-5 mt-6 space-y-3">
        {hideCodeField ? null : !defaultLoading && (
          <div>
            <Label className="text-xs">Church code *</Label>
            <Input
              data-testid="rm-code"
              required
              value={form.church_code}
              onChange={(e) => set("church_code", e.target.value.toUpperCase())}
              className="bg-white tracking-widest font-semibold"
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">First name *</Label>
            <Input
              data-testid="rm-first-name"
              required
              autoComplete="given-name"
              value={form.first_name}
              onChange={(e) => set("first_name", e.target.value)}
              className="bg-white"
            />
          </div>
          <div>
            <Label className="text-xs">Last name *</Label>
            <Input
              data-testid="rm-last-name"
              required
              autoComplete="family-name"
              value={form.last_name}
              onChange={(e) => set("last_name", e.target.value)}
              className="bg-white"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Phone number *</Label>
          <Input
            data-testid="rm-phone"
            required
            type="tel"
            minLength={5}
            autoComplete="tel"
            inputMode="tel"
            placeholder="+1 555 000 0000"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            className="bg-white"
          />
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Label className="text-xs">Date of birth *</Label>
            <span className="inline-flex items-center gap-1 text-[10.5px] text-stone-500">
              <Cake className="w-3 h-3" strokeWidth={1.8} />
              to celebrate with you 🎉
            </span>
          </div>
          <Input
            data-testid="rm-dob"
            required
            type="date"
            value={form.dob}
            onChange={(e) => set("dob", e.target.value)}
            className="bg-white"
          />
        </div>
        <div>
          <Label className="text-xs">Address <span className="text-stone-400">(optional)</span></Label>
          <Input
            data-testid="rm-address"
            autoComplete="street-address"
            placeholder="Street, City"
            value={form.address}
            onChange={(e) => set("address", e.target.value)}
            className="bg-white"
          />
        </div>
        <div>
          <Label className="text-xs">Email *</Label>
          <Input
            data-testid="rm-email"
            required
            type="email"
            autoComplete="email"
            // Prevent iOS Safari from silently capitalizing the first
            // character or auto-correcting the local-part of the
            // address — otherwise the account is stored as
            // "John@example.com" and later login attempts with
            // "john@example.com" show "Invalid credentials".
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="bg-white"
          />
        </div>
        <div>
          <Label className="text-xs">Password *</Label>
          <PasswordInput
            data-testid="rm-password"
            required
            minLength={6}
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            className="bg-white"
          />
        </div>
        <Button
          type="submit"
          disabled={
            loading ||
            !form.phone.trim() ||
            form.phone.trim().length < 5 ||
            !form.first_name.trim() ||
            !form.last_name.trim() ||
            !form.dob
          }
          data-testid="rm-submit"
          className="w-full rounded-full h-11 text-white"
          style={{ backgroundColor: "var(--tenant-primary)" }}
        >
          {loading ? "…" : "Create account"}
        </Button>
      </form>
      </>
      )}
    </div>
  );
}
